import {mergeMap, shareReplay, distinctUntilChanged, map, first, take} from 'rxjs/operators';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  NgZone,
  ChangeDetectorRef,
  Inject,
  forwardRef,
} from '@angular/core';
import {APPCONFIG} from '../../config';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {Observable} from 'rxjs';
import {Endpoint} from '../endpoint';
import {User} from '../../cache/currentuser/user';
import {CacheService} from '../../cache/cache.service';
import {Router} from '@angular/router';
import {LayoutService} from '../layout.service';
import {UIConfiguration} from '../../cache/uiconfiguration/uiconfiguration';
import {LanguageService} from '../../language/language.service';
import {IDMRequest} from '../../cache/request/request';
import {RequestService} from '../../cache/request/request.service';
import {ResourceService} from '../../resource/resource.service';
import {MsalService} from '@azure/msal-angular';

@Component({
  selector: 'my-app-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppHeaderComponent implements OnInit {
  public AppConfig: any;
  user: Observable<User>;
  requests: Observable<IDMRequest[]>;
  isCloud: boolean = false;

  administratorAccess: Observable<boolean>;
  devEnvironment: Observable<boolean>;
  devEnvironmentLabel: Observable<string>;

  color: Observable<string>;
  textColor: Observable<string>;
  siteTitle: Observable<string>;

  isChecked: boolean;

  constructor(
    private layoutService: LayoutService,
    private resourceService: ResourceService,
    private cacheService: CacheService,
    private router: Router,
    private ngZone: NgZone,
    private changeDetector: ChangeDetectorRef,
    private requestService: RequestService,
    private msalService: MsalService,
    private envConfigService: EnvironmentConfigService,
  ) {
    this.cacheService.getCurrentUser().subscribe((usr: User) => {
      this.isChecked = usr.darkmode;
      this.changeDetector.markForCheck();
    });

    // Subscribe to endpoint changes to track cloud status
    this.layoutService.endpoint$.subscribe((env: Endpoint) => {
      this.isCloud = env.isCloud();
      this.changeDetector.markForCheck();
    });
  }

  getContent(): void {
    this.devEnvironment = this.cacheService.getUIConfiguration().pipe(
      first(),
      map((uiconf: UIConfiguration) => uiconf.devenvironment),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.devEnvironmentLabel = this.cacheService
      .getLanguageResource()
      .pipe(map(lr => LanguageService.translate('HeaderDevEnvironment', 'Dev Environment', lr)));
    this.user = this.cacheService.getCurrentUser().pipe(first());
    this.administratorAccess = this.user.pipe(
      first(),
      map((usr: User) => usr.administrator),
      mergeMap(bool => bool),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.user.subscribe((user: User) => {
      this.requests = this.requestService.getRequests(user.id).pipe(
        map((requests: IDMRequest[]) => {
          return requests.sort(this.compare);
        }),
      );
    });
    this.color = this.cacheService.getUIConfiguration().pipe(
      first(),
      map((uiconf: UIConfiguration) => uiconf.color),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.textColor = this.cacheService.getUIConfiguration().pipe(
      first(),
      map((uiconf: UIConfiguration) => uiconf.textcolor),
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.siteTitle = this.cacheService.getUIConfiguration().pipe(
      first(),
      map((uiconf: UIConfiguration) => uiconf.sitetitle),
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.changeDetector.markForCheck();
  }

  ngOnInit(): void {
    this.AppConfig = APPCONFIG;
    this.getContent();
  }

  openProfile() {
    this.ngZone.runOutsideAngular(() => {
      let link = ['/home'];
      this.cacheService
        .getCurrentUser()
        .pipe(first())
        .subscribe(usr => (link = ['/get', usr.id]));
      this.ngZone.run(() => {
        this.router.navigate(link);
      });
    });
  }

  openFilterBuilder() {
    this.ngZone.runOutsideAngular(() => {
      let link = ['/filterbuilder'];
      this.ngZone.run(() => {
        this.router.navigate(link);
      });
    });
  }

  compare(a, b) {
    if (a.createdtime > b.createdtime) return -1;
    if (a.createdtime < b.createdtime) return 1;
    return 0;
  }

  openDetail(guid: string) {
    let link = ['/get', guid];
    if (this.router.isActive(link.join('/'), true)) {
      this.layoutService.updateResourceView('get', link[1], link[2]);
    } else {
      this.router.navigate(link);
    }
  }
  darkMode(): void {
    this.cacheService.getCurrentUser().subscribe((usr: User) => {
      let changes: any = {};
      const darkModeAttrName = this.envConfigService.getAttributeName('DarkMode');
      if (darkModeAttrName) {
        changes[darkModeAttrName] = this.isChecked;
      }
      this.resourceService.updateOrCreateResource(usr.id, changes, false).subscribe();
      this.layoutService.updateDarkMode(this.isChecked);
    });
  }
  darkModeText() {
    this.isChecked = !this.isChecked;
    this.darkMode();
  }

  logout(): void {
    this.layoutService.endpoint$.pipe(take(1)).subscribe((env: Endpoint) => {
      if (env.isCloud()) {
        this.cacheService.clearCache();

        const logoutRequest = {
          postLogoutRedirectUri: window.location.origin,
        };

        this.msalService.logoutRedirect(logoutRequest);
      }
    });
  }
}
